import { EstimationMethod } from '@shared/modals/vee/estimation-method.enum';

export interface EstimatedTimeSeriesCount {
  date?: string | null;
  estimationMethod?: string | null;
  count?: number | null;
}

export interface EstimatedTimeSeriesChartSeries {
  method: string;
  name: string;
  color: string;
  data: number[];
}

export interface EstimatedTimeSeriesChartData {
  dates: string[];
  series: EstimatedTimeSeriesChartSeries[];
  totals: number[];
}

const estimationMethodColors = {
  NotSpecified: '#64748b',
  SameAsDayBefore: '#2563eb',
  SameAsSevenDaysBefore: '#7c3aed',
  ControlMeter: '#0891b2',
  SameAs1MonthBefore: '#db2777',
  GhostMeter: '#d97706',
  ZeroValueMeteringPointDisconnected: '#dc2626',
  ZeroValueInsideDay: '#ea580c',
  ManualNotSpecified: '#475569',
  ManualEvenly: '#ca8a04',
  ManualLastPosition: '#9333ea',
  ManualProfile: '#0d9488',
  ManualOtherMeteringPoint: '#4f46e5',
  AdvancedVEE: '#059669',
  ManualAdvancedVEE: '#c026d3',
  AdvancedVEELookBack: '#0284c7',
  AdvancedVEEClustering: '#65a30d',
  AdvancedVEEEVHandler: '#e11d48',
  ZeroValueMeterChange: '#a16207',
} satisfies Record<keyof typeof EstimationMethod, string>;

const unknownEstimationMethodColor = '#334155';

export function buildEstimatedTimeSeriesChartData(
  rows: EstimatedTimeSeriesCount[],
  formatDate: (date: string) => string,
  getMethodLabel: (method: string) => string,
): EstimatedTimeSeriesChartData {
  const rawDates = Array.from(new Set(
    rows.map(row => row.date).filter((date): date is string => Boolean(date)),
  )).sort((left, right) => left.localeCompare(right));
  const methods = Array.from(new Set(
    rows
      .filter(row => Number(row.count ?? 0) > 0)
      .map(row => row.estimationMethod)
      .filter((method): method is string => Boolean(method)),
  )).sort((left, right) => left.localeCompare(right));

  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.date || !row.estimationMethod) {
      continue;
    }

    const key = `${row.date}\u0000${row.estimationMethod}`;
    counts.set(key, (counts.get(key) ?? 0) + Number(row.count ?? 0));
  }

  const series = methods.map(method => ({
    method,
    name: getMethodLabel(method),
    color: getEstimationMethodColor(method),
    data: rawDates.map(date => counts.get(`${date}\u0000${method}`) ?? 0),
  }));
  const totals = rawDates.map((_, index) =>
    series.reduce((total, item) => total + item.data[index], 0),
  );

  return {
    dates: rawDates.map(formatDate),
    series,
    totals,
  };
}

export function getEstimationMethodColor(method: string): string {
  return Object.hasOwn(estimationMethodColors, method)
    ? estimationMethodColors[method as keyof typeof estimationMethodColors]
    : unknownEstimationMethodColor;
}
